import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setCurrentPage, setDownloadAppBar } from "../../feature/userData/userData";
import submitAgentLinkLead from "../../service/api/user/submitAgentLinkLead";
import FunnelVideo from "./FunnelVideo";
import NotYetLeadForm from "./NotYetLeadForm";
import { FUNNEL_PROBLEM_KEYS, FUNNEL_PROBLEMS } from "./funnelProblems";
import "./addLink.scss";

const STEP_PROBLEM = 1;
const STEP_BARRIER = 2;
const STEP_CTA = 3;

const FUNNEL_STEPS = [
  { id: STEP_PROBLEM, label: "Problem" },
  { id: STEP_BARRIER, label: "Why" },
  { id: STEP_CTA, label: "Ready" },
];

function FunnelProgress({ step }) {
  return (
    <nav className="add-link-funnel-progress" aria-label="Funnel progress">
      {FUNNEL_STEPS.map((s, i) => (
        <React.Fragment key={s.id}>
          <div
            className={`add-link-funnel-progress__item${
              step >= s.id ? " is-done" : ""
            }${step === s.id ? " is-current" : ""}`}
          >
            <span className="add-link-funnel-progress__dot">{i + 1}</span>
            <span className="add-link-funnel-progress__label">{s.label}</span>
          </div>
          {i < FUNNEL_STEPS.length - 1 && (
            <span
              className={`add-link-funnel-progress__line${
                step > s.id ? " is-done" : ""
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

function FunnelStepLayout({
  showBack,
  onBack,
  header,
  showChecks,
  subtext,
  pillsLayout = "stack",
  children,
}) {
  return (
    <div className="add-link-funnel-step">
      {showBack && (
        <div className="add-link-funnel-toolbar">
          <button
            type="button"
            className="add-link-funnel-back"
            onClick={onBack}
            aria-label="Go back"
          >
            ← Back
          </button>
        </div>
      )}
      <div className="add-link-funnel-body">
        <div className="add-link-funnel-media">
          <h1 className="add-link-funnel-header">{header}</h1>
          <div className="add-link-funnel-video">
            <FunnelVideo showChecks={showChecks} />
          </div>
        </div>
        <div className="add-link-funnel-actions">
          {subtext && <p className="add-link-funnel-subtext">{subtext}</p>}
          <div className={`add-link-funnel-pills add-link-funnel-pills--${pillsLayout}`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

const AddLink = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [step, setStep] = useState(STEP_PROBLEM);
  const [problemKey, setProblemKey] = useState(null);
  const [barrierAnswer, setBarrierAnswer] = useState(null);
  const [animating, setAnimating] = useState(false);
  const [leadFormOpen, setLeadFormOpen] = useState(false);

  const problem = problemKey ? FUNNEL_PROBLEMS[problemKey] : null;

  useEffect(() => {
    dispatch(setCurrentPage("Agent link"));
    dispatch(setDownloadAppBar(false));
  }, [dispatch]);

  const goToStep = (nextStep) => {
    setAnimating(true);
    setTimeout(() => {
      setStep(nextStep);
      setAnimating(false);
    }, 180);
  };

  const openLeadForm = () => {
    if (step !== STEP_CTA) return;
    setLeadFormOpen(true);
  };

  const closeLeadForm = () => setLeadFormOpen(false);

  const selectProblem = (key) => {
    setProblemKey(key);
    setBarrierAnswer(null);
    goToStep(STEP_BARRIER);
  };

  const selectBarrier = (label) => {
    setBarrierAnswer(label);
    goToStep(STEP_CTA);
  };

  const goBack = () => {
    setLeadFormOpen(false);
    if (step === STEP_BARRIER) {
      setProblemKey(null);
      goToStep(STEP_PROBLEM);
    } else if (step === STEP_CTA) {
      setBarrierAnswer(null);
      goToStep(STEP_BARRIER);
    }
  };

  const buildAnswers = () => {
    if (!problem) return [];
    return [
      {
        id: "home_problem",
        prompt: "What Problem Are You Having at Your House?",
        answer: `${problem.emoji} ${problem.label}`,
      },
      {
        id: "barrier",
        prompt: problem.barrierPrompt,
        answer: barrierAnswer,
      },
    ];
  };

  const handleLeadSubmit = (fields) => {
    const problemAnswer = problem
      ? `${problem.emoji} ${problem.label}`
      : "";
    return new Promise((resolve, reject) => {
      dispatch(
        submitAgentLinkLead(
          {
            name: fields.name,
            email: fields.email,
            phone: fields.phone,
            agent_link_problem_chr: problemAnswer,
            agent_link_source_chr: "meta_agent_link",
          },
          () => resolve(),
          () => reject(new Error("submit failed"))
        )
      );
    });
  };

  const sendToScanNew = () => {
    navigate("/scanNew", {
      state: {
        agentLinkAnswers: buildAnswers(),
        agentLinkCompletedAt: new Date().toISOString(),
      },
    });
  };

  const barrierHeader = problem ? (
    <>
      {problem.header.split("\n").map((line, i, arr) => (
        <React.Fragment key={line}>
          {line}
          {i < arr.length - 1 && <br />}
        </React.Fragment>
      ))}
    </>
  ) : null;

  let stepContent = null;

  if (step === STEP_PROBLEM) {
    stepContent = (
      <FunnelStepLayout
        header="What Problem Are You Having at Your House?"
        pillsLayout="grid"
      >
        {FUNNEL_PROBLEM_KEYS.map((key) => {
          const p = FUNNEL_PROBLEMS[key];
          return (
            <button
              key={key}
              type="button"
              className="add-link-pill-btn"
              onClick={() => selectProblem(key)}
            >
              {p.emoji}&nbsp;&nbsp;{p.label}
            </button>
          );
        })}
      </FunnelStepLayout>
    );
  } else if (step === STEP_BARRIER && problem) {
    stepContent = (
      <FunnelStepLayout
        showBack
        onBack={goBack}
        header={barrierHeader}
        pillsLayout="stack"
      >
        {problem.pills.map((label) => (
          <button
            key={label}
            type="button"
            className="add-link-pill-btn"
            onClick={() => selectBarrier(label)}
          >
            {label}
          </button>
        ))}
      </FunnelStepLayout>
    );
  } else if (step === STEP_CTA) {
    stepContent = (
      <FunnelStepLayout
        showBack
        onBack={goBack}
        header="We Can Diagnose What&apos;s Going On — Remotely 🏠"
        showChecks
        subtext="Our experts will review your issue on video and help you understand what&apos;s wrong — no app, no login needed."
        pillsLayout="cta"
      >
        <button
          type="button"
          className="add-link-pill-btn add-link-pill-btn--success"
          onClick={sendToScanNew}
        >
          🚀&nbsp;&nbsp;I&apos;m Ready for a Free Diagnosis
        </button>
        <button
          type="button"
          className="add-link-pill-btn add-link-pill-btn--secondary"
          onClick={openLeadForm}
        >
          📅&nbsp;&nbsp;Not Yet, But Check Back
        </button>
      </FunnelStepLayout>
    );
  }

  return (
    <div className="add-link-funnel-page">
      <div className="add-link-funnel-overlay">
        {leadFormOpen && (
          <NotYetLeadForm
            onClose={closeLeadForm}
            onSubmit={handleLeadSubmit}
            onDone={() => {
              closeLeadForm();
              navigate("/");
            }}
          />
        )}
        <div className="add-link-funnel-shell">
          <header className="add-link-funnel-top">
            <div className="add-link-funnel-brand">
              <span className="add-link-funnel-brand-mark">Chilll</span>
              <span className="add-link-funnel-brand-sub">Agent link</span>
            </div>
            <FunnelProgress step={step} />
          </header>
          <div
            className={`add-link-funnel-card${
              animating ? " add-link-funnel-card--anim" : ""
            }`}
            key={step}
          >
            <div className="add-link-funnel-handle" aria-hidden />
            {stepContent}
            <a
              href="https://chilll.com"
              target="_blank"
              rel="noopener noreferrer"
              className="add-link-funnel-logo"
            >
              CHILLL.COM
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddLink;
